import type { VaelorApi } from "./api";
import {
  commandForSiegeAction,
  siegeErrorMessage,
  SiegeTransport,
  type SiegeTransportHandlers,
} from "./siege";
import type {
  SiegeConnectionState,
  SiegeSnapshot,
  SiegeSocketEvent,
  SiegeUiAction,
} from "../types";

export type HeartFrontPublicId =
  | "heart-front-1"
  | "heart-front-2"
  | "heart-front-3"
  | "heart-front-4"
  | "heart-front-5"
  | "heart-front-6";

export type HeartFrontPublicStatus =
  | "sealed"
  | "exposed"
  | "assault-ready"
  | "unavailable"
  | "not-connected";

export interface HeartFrontPublicEntry {
  frontId: HeartFrontPublicId;
  status: HeartFrontPublicStatus;
}

export interface HeartFrontProjection {
  schemaVersion: 1;
  revision: number;
  fronts: HeartFrontPublicEntry[];
}

export interface HeartFrontSessionState {
  connection: SiegeConnectionState;
  activeFrontId?: HeartFrontPublicId;
  snapshot?: SiegeSnapshot;
  error?: string;
  projection: HeartFrontProjection;
}

export type HeartFrontSessionListener = (state: HeartFrontSessionState) => void;

export type HeartFrontTransportFactory = (
  api: VaelorApi,
  webSocketBase: string,
  handlers: SiegeTransportHandlers,
) => SiegeTransport;

export const HEART_FRONT_PUBLIC_IDS: readonly HeartFrontPublicId[] = [
  "heart-front-1",
  "heart-front-2",
  "heart-front-3",
  "heart-front-4",
  "heart-front-5",
  "heart-front-6",
];

export const HEART_FRONT_PRIVATE_SESSION_IDS: readonly HeartFrontPublicId[] = [
  "heart-front-1",
  "heart-front-2",
  "heart-front-3",
  "heart-front-4",
  "heart-front-5",
  "heart-front-6",
];

const DEFAULT_ERROR = "La liaison avec le Front du Cœur est indisponible.";
const INVALID_EVENT_ERROR = "Le serveur a envoyé un événement du Front actif invalide.";
const EXPIRED_TICKET_ERROR = "Le ticket du Front du Cœur a expiré avant l’ouverture.";

function defaultTransportFactory(
  api: VaelorApi,
  webSocketBase: string,
  handlers: SiegeTransportHandlers,
): SiegeTransport {
  return new SiegeTransport(api, webSocketBase, handlers);
}

export function createHeartFrontProjection(
  revision: number,
  front1Status: Exclude<HeartFrontPublicStatus, "not-connected">,
): HeartFrontProjection {
  return {
    schemaVersion: 1,
    revision,
    fronts: HEART_FRONT_PUBLIC_IDS.map((frontId, index) => ({
      frontId,
      status: index === 0 ? front1Status : "not-connected",
    })),
  };
}

function unavailableProjection(revision = 0): HeartFrontProjection {
  return createHeartFrontProjection(revision, "unavailable");
}

function initialState(activeFrontId?: HeartFrontPublicId): HeartFrontSessionState {
  return {
    connection: "idle",
    activeFrontId,
    projection: unavailableProjection(),
  };
}

function normalizeActiveFrontId(value: HeartFrontPublicId | undefined): HeartFrontPublicId | undefined {
  return typeof value === "string" && HEART_FRONT_PRIVATE_SESSION_IDS.includes(value)
    ? value
    : undefined;
}

export class HeartFrontSession {
  private api?: VaelorApi;
  private webSocketBase?: string;
  private readonly transportFactory: HeartFrontTransportFactory;
  private readonly listeners = new Set<HeartFrontSessionListener>();
  private state: HeartFrontSessionState;
  private transport?: SiegeTransport;
  private active = false;
  private activeFrontId?: HeartFrontPublicId;
  private generation = 0;
  private lastRevision?: number;

  constructor(
    api?: VaelorApi,
    webSocketBase?: string,
    transportFactory?: HeartFrontTransportFactory,
    activeFrontId?: HeartFrontPublicId,
  );
  constructor(
    api?: VaelorApi,
    webSocketBase?: string,
    activeFrontId?: HeartFrontPublicId,
    transportFactory?: HeartFrontTransportFactory,
  );
  constructor(
    api?: VaelorApi,
    webSocketBase?: string,
    third?: HeartFrontTransportFactory | HeartFrontPublicId,
    fourth?: HeartFrontTransportFactory | HeartFrontPublicId,
  ) {
    this.api = api;
    this.webSocketBase = webSocketBase;
    this.transportFactory = typeof third === "function"
      ? third
      : typeof fourth === "function"
        ? fourth
        : defaultTransportFactory;
    const candidate = typeof third === "string" ? third : typeof fourth === "string" ? fourth : undefined;
    this.activeFrontId = normalizeActiveFrontId(candidate);
    this.state = initialState(this.activeFrontId);
  }

  configure(api: VaelorApi, webSocketBase: string, activeFrontId?: HeartFrontPublicId): void {
    const nextActiveFrontId = normalizeActiveFrontId(activeFrontId);
    const activeFrontChanged = nextActiveFrontId !== this.activeFrontId;
    const transportConfigurationChanged = this.api !== api || this.webSocketBase !== webSocketBase;
    this.api = api;
    this.webSocketBase = webSocketBase;
    if (activeFrontChanged) {
      this.replaceActiveFront(nextActiveFrontId);
    } else if (transportConfigurationChanged && this.active && this.activeFrontId) {
      this.beginConnection();
    }
  }

  setActiveFrontId(activeFrontId?: HeartFrontPublicId): void {
    const nextActiveFrontId = normalizeActiveFrontId(activeFrontId);
    if (nextActiveFrontId === this.activeFrontId) return;
    this.replaceActiveFront(nextActiveFrontId);
  }

  setPublicProjection(projection: HeartFrontProjection): void {
    this.publish({
      ...this.state,
      projection,
    });
  }

  getState(): HeartFrontSessionState {
    return this.state;
  }

  subscribe(listener: HeartFrontSessionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  start(activeFrontId?: HeartFrontPublicId): void {
    if (arguments.length > 0) this.setActiveFrontId(activeFrontId);
    if (this.active) return;
    this.active = true;
    if (!this.activeFrontId) {
      this.publish({
        connection: "idle",
        snapshot: undefined,
        error: undefined,
        projection: this.state.projection,
      });
      return;
    }
    this.beginConnection();
  }

  retry(): void {
    if (!this.active) return;
    this.beginConnection();
  }

  stop(): void {
    this.active = false;
    this.generation += 1;
    this.disposeTransport();
    this.lastRevision = undefined;
    this.publish({
      connection: "idle",
      snapshot: undefined,
      error: undefined,
      projection: this.state.projection,
    });
  }

  sendAction(action: SiegeUiAction): string {
    const transport = this.transport;
    const activeFrontId = this.activeFrontId;
    const snapshot = this.state.snapshot;
    if (
      !this.active ||
      !activeFrontId ||
      !transport ||
      this.state.connection !== "connected" ||
      !snapshot ||
      snapshot.roomId !== activeFrontId
    ) {
      throw new Error("siege_not_connected");
    }
    const commandId = transport.send(commandForSiegeAction(action));
    transport.requestSnapshot();
    return commandId;
  }

  private beginConnection(): void {
    const activeFrontId = this.activeFrontId;
    if (!this.active || !activeFrontId) return;
    const generation = ++this.generation;
    this.disposeTransport();
    this.lastRevision = undefined;
    this.publish({
      connection: "connecting",
      snapshot: undefined,
      error: undefined,
      projection: this.state.projection,
    });

    const api = this.api;
    const webSocketBase = this.webSocketBase;
    if (!api || !webSocketBase) {
      this.publish({
        connection: "error",
        error: DEFAULT_ERROR,
        projection: this.state.projection,
      });
      return;
    }

    let transport: SiegeTransport | undefined;
    try {
      transport = this.transportFactory(api, webSocketBase, {
        onEvent: (event) => {
          if (!transport || !this.isCurrent(generation, transport, activeFrontId)) return;
          this.handleEvent(generation, transport, activeFrontId, event);
        },
        onOpen: () => {
          if (!transport || !this.isCurrent(generation, transport, activeFrontId)) return;
          this.publish({
            ...this.state,
            connection: "connected",
            error: undefined,
          });
        },
        onClose: (event) => {
          if (!transport || !this.isCurrent(generation, transport, activeFrontId)) return;
          this.transport = undefined;
          this.publish({
            connection: event.code === 1000 ? "closed" : "error",
            error: event.code === 1000 ? undefined : "La liaison avec le Front du Cœur a été interrompue.",
            snapshot: undefined,
            projection: this.state.projection,
          });
        },
        onError: () => {
          if (!transport || !this.isCurrent(generation, transport, activeFrontId)) return;
          this.failClosed(generation, transport, activeFrontId, DEFAULT_ERROR);
        },
        onInvalidEvent: () => {
          if (!transport || !this.isCurrent(generation, transport, activeFrontId)) return;
          this.failClosed(generation, transport, activeFrontId, INVALID_EVENT_ERROR);
        },
        onTicketExpired: () => {
          if (!transport || !this.isCurrent(generation, transport, activeFrontId)) return;
          this.failClosed(generation, transport, activeFrontId, EXPIRED_TICKET_ERROR);
        },
      });
      this.transport = transport;
    } catch (cause) {
      if (this.active && this.generation === generation) {
        this.publish({
          connection: "error",
          error: cause instanceof Error ? cause.message : DEFAULT_ERROR,
          projection: this.state.projection,
        });
      }
      return;
    }

    if (!transport) return;
    void transport.connect(activeFrontId).catch((cause: unknown) => {
      if (!this.isCurrent(generation, transport, activeFrontId)) return;
      this.failClosed(
        generation,
        transport,
        activeFrontId,
        cause instanceof Error ? cause.message : DEFAULT_ERROR,
      );
    });
  }

  private handleEvent(
    generation: number,
    transport: SiegeTransport,
    activeFrontId: HeartFrontPublicId,
    event: SiegeSocketEvent,
  ): void {
    if (event.type === "siege.snapshot") {
      if (event.roomId !== activeFrontId) {
        this.failClosed(generation, transport, activeFrontId, INVALID_EVENT_ERROR);
        return;
      }
      if (this.lastRevision !== undefined && event.revision <= this.lastRevision) return;
      this.lastRevision = event.revision;
      this.publish({
        connection: "connected",
        snapshot: event,
        error: undefined,
        projection: this.state.projection,
      });
      return;
    }
    if (event.type === "siege.error") {
      this.publish({
        ...this.state,
        error: siegeErrorMessage(event.error),
      });
    }
  }

  private failClosed(
    generation: number,
    transport: SiegeTransport,
    activeFrontId: HeartFrontPublicId,
    error: string,
  ): void {
    if (!this.isCurrent(generation, transport, activeFrontId)) return;
    this.generation += 1;
    this.transport = undefined;
    transport.close();
    this.publish({
      connection: "error",
      error,
      snapshot: undefined,
      projection: this.state.projection,
    });
  }

  private isCurrent(
    generation: number,
    transport: SiegeTransport,
    activeFrontId: HeartFrontPublicId,
  ): boolean {
    return this.active &&
      this.activeFrontId === activeFrontId &&
      this.generation === generation &&
      this.transport === transport;
  }

  private replaceActiveFront(activeFrontId: HeartFrontPublicId | undefined): void {
    this.activeFrontId = activeFrontId;
    this.generation += 1;
    this.disposeTransport();
    this.lastRevision = undefined;
    if (this.active && activeFrontId) {
      this.beginConnection();
      return;
    }
    this.publish({
      connection: "idle",
      snapshot: undefined,
      error: undefined,
      projection: this.state.projection,
    });
  }

  private disposeTransport(): void {
    const transport = this.transport;
    this.transport = undefined;
    transport?.close();
  }

  private publish(nextState: HeartFrontSessionState): void {
    this.state = {
      ...nextState,
      activeFrontId: this.activeFrontId,
    };
    for (const listener of this.listeners) listener(this.state);
  }
}
